/**
 * Playsout API 服务
 * 处理与 Playsout 后端的所有 API 交互
 */

const BASE_URL = 'https://api.playsout.com';
const LOGIN_MESSAGE = 'Welcome to the PlaysOut Airdrop! Sign to verify your identity and start your reward journey.';

// localStorage keys
const STORAGE_KEYS = {
  TOKEN: 'playsout_token',
  INVITE_CODE: 'playsout_invite_code',
  WALLET_ADDRESS: 'playsout_wallet_address',
};


interface LoginResponse {
  code: number;
  msg: string;
  data: {
    userToken: string;
    botName?: string;
    id: number;
    exp: number;
    platform: string;
  };
  times: number;
}

interface InviteCodeResponse {
  code: number;
  msg: string; // invite code is in msg field
  times: number;
}

/**
 * Playsout API 类
 */
export class PlaysoutAPI {
  private token: string | null = null;
  private walletAddress: string | null = null;
  private inviteCode: string | null = null;

  constructor() {
    // 从 localStorage 加载已保存的数据
    if (typeof window !== 'undefined') {
      this.token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      this.walletAddress = localStorage.getItem(STORAGE_KEYS.WALLET_ADDRESS);
      this.inviteCode = localStorage.getItem(STORAGE_KEYS.INVITE_CODE);
    }
  }

  /**
   * 检查是否已登录
   */
  isLoggedIn(): boolean {
    return !!this.token && !!this.walletAddress;
  }

  /**
   * 获取当前token
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * 获取invite code
   */
  getInviteCode(): string | null {
    return this.inviteCode;
  }

  /**
   * 确保 token 已加载，如果内存中没有则尝试从 localStorage 读取
   * 解决跨页面导航时单例状态不同步的问题
   */
  ensureToken(): string | null {
    if (!this.token && typeof window !== 'undefined') {
      const storedToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
      if (storedToken) {
        this.token = storedToken;
        this.walletAddress = localStorage.getItem(STORAGE_KEYS.WALLET_ADDRESS);
        this.inviteCode = localStorage.getItem(STORAGE_KEYS.INVITE_CODE);
        console.log('[PlaysoutAPI] Token restored from localStorage');
      }
    }
    return this.token;
  }

  /**
   * 检查钱包地址是否变更，如果变更则清除缓存
   */
  checkWalletChange(currentAddress: string): boolean {
    const normalizedAddress = currentAddress.toLowerCase();
    if (this.walletAddress && this.walletAddress !== normalizedAddress) {
      // 钱包变更，清除所有缓存
      this.clearSession();
      return true;
    }
    return false;
  }

  /**
   * 清除会话数据
   */
  clearSession(): void {
    this.token = null;
    this.walletAddress = null;
    this.inviteCode = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.WALLET_ADDRESS);
      localStorage.removeItem(STORAGE_KEYS.INVITE_CODE);
    }
  }

  /**
   * 保存会话数据
   */
  private saveSession(token: string, address: string): void {
    this.token = token;
    this.walletAddress = address;
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.WALLET_ADDRESS, address);
    }
  }

  /**
   * 保存invite code
   */
  private saveInviteCode(code: string): void {
    this.inviteCode = code;
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.INVITE_CODE, code);
    }
  }

  /**
   * 1. 登录接口
   * POST /user/login
   * 
   * @param address - 钱包地址
   * @param signMessage - 签名函数，由调用方提供
   * @param inviteCode - 可选的邀请码
   */
  async login(
    address: string,
    signMessage: (message: string) => Promise<string>,
    inviteCode?: string
  ): Promise<string> {
    const normalizedAddress = address.toLowerCase();

    // 如果已登录且地址相同，直接返回token
    if (this.token && this.walletAddress === normalizedAddress) {
      console.log('[PlaysoutAPI] Already logged in, returning cached token');
      return this.token;
    }

    // 检查钱包是否变更
    this.checkWalletChange(normalizedAddress);

    try {
      // 对消息进行签名
      const signature = await signMessage(LOGIN_MESSAGE);

      const response = await fetch(`${BASE_URL}/user/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bscLoginRequest: {
            message: LOGIN_MESSAGE,
            address: normalizedAddress,
            signature: signature,
          },
          inviteCode: inviteCode || '',
        }),
      });

      if (!response.ok) {
        throw new Error(`Login failed: HTTP ${response.status}`);
      }

      const data: LoginResponse = await response.json();

      // API 返回 code: 10000 表示成功
      if (data.code !== 10000) {
        throw new Error(data.msg || 'Login failed');
      }

      const userToken = data.data.userToken;
      this.saveSession(userToken, normalizedAddress);
      
      console.log('[PlaysoutAPI] Login successful');
      return userToken;
    } catch (error) {
      console.error('[PlaysoutAPI] Login error:', error);
      throw error;
    }
  }

  /**
   * 2. 心跳接口
   * GET /game/heart?gameId=xxx
   * 
   * @param gameId - 游戏ID
   */
  async sendHeartbeat(gameId: string): Promise<boolean> {
    // 确保 token 已加载
    const token = this.ensureToken();
    if (!token) {
      console.warn('[PlaysoutAPI] Cannot send heartbeat: not logged in');
      return false;
    }

    try {
      const response = await fetch(`${BASE_URL}/game/heart?gameId=${encodeURIComponent(gameId)}`, {
        method: 'GET',
        headers: {
          'token': token,
        },
      });

      const success = response.ok;
      if (success) {
        console.log('[PlaysoutAPI] Heartbeat sent successfully for game:', gameId);
      } else {
        console.warn('[PlaysoutAPI] Heartbeat failed with status:', response.status);
      }
      
      return success;
    } catch (error) {
      console.error('[PlaysoutAPI] Heartbeat error:', error);
      return false;
    }
  }

  /**
   * 3. 获取邀请码接口
   * GET /game/inviteCode
   */
  async fetchInviteCode(): Promise<string | null> {
    const token = this.ensureToken();
    if (!token) {
      console.warn('[PlaysoutAPI] Cannot fetch invite code: not logged in');
      return null;
    }

    // 如果已有缓存的invite code，直接返回
    if (this.inviteCode) {
      console.log('[PlaysoutAPI] Returning cached invite code');
      return this.inviteCode;
    }

    try {
      const response = await fetch(`${BASE_URL}/game/inviteCode`, {
        method: 'GET',
        headers: {
          'token': token,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch invite code: HTTP ${response.status}`);
      }

      const data: InviteCodeResponse = await response.json();

      // API 返回 code: 10000 表示成功，invite code 在 msg 字段
      if (data.code !== 10000) {
        throw new Error('Failed to fetch invite code');
      }

      const code = data.msg; // invite code is in msg field
      this.saveInviteCode(code);
      
      console.log('[PlaysoutAPI] Invite code fetched successfully');
      return code;
    } catch (error) {
      console.error('[PlaysoutAPI] Fetch invite code error:', error);
      return null;
    }
  }

  /**
   * 4. 报告购买/签到交易
   * GET /game/buy?gameId=xxx&hash=xxx
   * 
   * @param gameId - 游戏ID
   * @param hash - 交易hash
   */
  async reportBuy(gameId: string, hash: string): Promise<boolean> {
    const token = this.ensureToken();
    if (!token) {
      console.warn('[PlaysoutAPI] Cannot report buy: not logged in');
      return false;
    }

    try {
      const response = await fetch(
        `${BASE_URL}/game/buy?gameId=${encodeURIComponent(gameId)}&hash=${encodeURIComponent(hash)}`,
        {
          method: 'GET',
          headers: {
            'token': token,
          },
        }
      );

      const success = response.ok;
      if (success) {
        console.log('[PlaysoutAPI] Buy reported successfully:', { gameId, hash });
      } else {
        console.warn('[PlaysoutAPI] Report buy failed with status:', response.status);
      }

      return success;
    } catch (error) {
      console.error('[PlaysoutAPI] Report buy error:', error);
      return false;
    }
  }
}

// 导出单例实例
let playsoutApiInstance: PlaysoutAPI | null = null;

export function getPlaysoutAPI(): PlaysoutAPI {
  if (!playsoutApiInstance) {
    playsoutApiInstance = new PlaysoutAPI();
  }
  return playsoutApiInstance;
}

// 导出用于重置实例的函数（主要用于测试）
export function resetPlaysoutAPI(): void {
  if (playsoutApiInstance) {
    playsoutApiInstance.clearSession();
  }
  playsoutApiInstance = null;
}
